// src/pages/PolicyDetailView.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  IconButton,
  Fade,
  Skeleton,
  Paper,
  Chip,
  Container,
  useTheme,
  alpha
} from '@mui/material';
import { ArrowBack, Description, AccessTime } from '@mui/icons-material';
import axios from 'axios';
import Try from '../../charts/try';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getPolicyById } from '../../services/policyAPIs';

function decodeHtml(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

const PolicyDetailView = () => {
  //const { id } = useParams();
  const location = useLocation();
  const { id } = location.state || {};

  const navigate = useNavigate();
  const theme = useTheme();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const data = await getPolicyById(id);
        setPolicy(data);
      } catch (err) {
        console.error('Failed to load policy:', err);
        setError('Failed to load policy.');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchPolicy();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: '50%' }} />
        </Box>
        <Card
          elevation={0}
          sx={{
            borderRadius: '24px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.background.paper, 0.8)} 0%, 
              ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Skeleton variant="text" sx={{ fontSize: '2.5rem', mb: 3, mx: 'auto', width: '60%' }} />
            <Skeleton variant="rectangular" height={2} sx={{ mb: 3 }} />
            <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={300} sx={{ mt: 3, borderRadius: '16px' }} />
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error || !policy) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Fade in>
          <Card
            elevation={0}
            sx={{
              borderRadius: '24px',
              textAlign: 'center',
              p: 6,
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.error.light, 0.1)} 0%, 
                ${alpha(theme.palette.error.light, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            }}
          >
            <Typography 
              variant="h4" 
              color="error.main"
              sx={{ 
                mb: 2,
                fontWeight: 600,
              }}
            >
              Oops! Something went wrong
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ mb: 4, fontSize: '1.1rem' }}
            >
              {error || 'The policy you\'re looking for could not be found.'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/policies',  { state: { activeTab: 'opolicies' } })}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
              }}
            >
              Go Back
            </Button>
          </Card>
        </Fade>
      </Container>
    );
  }

  const { title, body, imageUrls, up } = policy;

  // Detect if content is numbered (same logic as in PolicyCard)
  const lines = body.split('\n').filter(line => line.trim() !== '');
  const isNumbered = lines.some(line => /^[\s\u00A0]*(\d+\.\s+)(.*)/.test(line));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minWidth: '100%',
        // background: `linear-gradient(135deg, 
        //   ${alpha(theme.palette.primary.main, 0.02)} 0%, 
        //   ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        // py: 4,
      }}
    >
      <Container maxWidth="100%">
        <Fade in timeout={800}>
          <Box>
            {/* Header with back button */}
            <Box 
              sx={{ 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <IconButton
                // onClick={() => navigate(-1)}
                onClick={() => navigate('/policies',  { state: { activeTab: 'opolicies' } })}
                sx={{
                  background: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  '&:hover': {
                    background: alpha(theme.palette.background.paper, 0.9),
                    transform: 'translateX(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                aria-label="go back"
              >
                <ArrowBack />
              </IconButton>
              <Chip
                icon={<Description />}
                label="Policy Details"
                variant="outlined"
                sx={{
                  background: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Main content card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: '24px',
                overflow: 'hidden',
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.background.paper, 0.9)} 0%, 
                  ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.08)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.12)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                {/* Title section */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, 
                        ${theme.palette.primary.main} 0%, 
                        ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2,
                      lineHeight: 1.2,
                      fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                    }}
                  >
                    {title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {up === 'hr' && (
                      <Chip
                        label="HR Policy"
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isNumbered && (
                      <Chip
                        label="Structured Content"
                        color="secondary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    <Chip
                      icon={<AccessTime fontSize="small" />}
                      label={`Policy ID: ${id}`}
                      variant="outlined"
                      size="small"
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}
                    />
                  </Box>
                </Box>

                {/* Elegant divider */}
                <Box
                  sx={{
                    height: '2px',
                    background: `linear-gradient(90deg, 
                      transparent 0%, 
                      ${theme.palette.primary.main} 50%, 
                      transparent 100%)`,
                    mb: 4,
                    borderRadius: '1px',
                  }}
                />

                {/* Content section */}
                {isNumbered && up === 'hr' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      gap: 4,
                      mt: 2,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 3,
                        borderRadius: '16px',
                        background: alpha(theme.palette.background.default, 0.5),
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        '& p': { 
                          mb: '1.2em',
                          lineHeight: 1.7,
                          fontSize: '1.05rem',
                        },
                        '& ul, & ol': { 
                          mt: '0.8em', 
                          mb: '1.2em', 
                          pl: '2em',
                        },
                        '& li': { 
                          mb: '0.8em',
                          lineHeight: 1.6,
                        },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mt: '1.5em',
                          mb: '0.8em',
                        },
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: decodeHtml(body) }} />
                    </Paper>
                    <Paper
                      elevation={0}
                      sx={{
                        width: { xs: '100%', lg: '45%' },
                        p: 3,
                        borderRadius: '16px',
                        background: alpha(theme.palette.secondary.light, 0.05),
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Try text={body} title={title} />
                    </Paper>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', lg: 'row' },
                      gap: 4,
                      mt: 2,
                    }}
                  >
                    {body.trim() && (
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1,
                          p: 3,
                          borderRadius: '16px',
                          background: alpha(theme.palette.background.default, 0.5),
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          '& p': { 
                            mb: '1.2em',
                            lineHeight: 1.7,
                            fontSize: '1.05rem',
                            color: theme.palette.text.primary,
                          },
                          '& ul, & ol': { 
                            mt: '0.8em', 
                            mb: '1.2em', 
                            pl: '2em',
                          },
                          '& li': { 
                            mb: '0.8em',
                            lineHeight: 1.6,
                          },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            mt: '1.5em',
                            mb: '0.8em',
                          },
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: decodeHtml(body) }} />
                      </Paper>
                    )}
                    {imageUrls && imageUrls.length > 0 && (
                      <Box
                        sx={{
                          width: body.trim() ? { xs: '100%', lg: '45%' } : '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        {imageUrls.map((url, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              borderRadius: '16px',
                              overflow: 'hidden',
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                              },
                            }}
                          >
                            <img
                              src={url}
                              alt={`Policy illustration ${index + 1}`}
                              style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                              }}
                              loading="lazy"
                            />
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default PolicyDetailView;